import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom, from, lastValueFrom, of } from 'rxjs';
import { User } from './user.model';
import { FirebaseApiProvider } from 'libs/api/providers/firebase-api.provider';
import { AuthService } from 'libs/auth/auth.service';
import { debounceTime, filter, finalize, map, switchMap, take, takeLast, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UnsubscriberService } from 'libs/unsubcriber/unsubscriber.service';
import { PushNotificationService } from 'apps/craf2s-client-app/src/app/services/push-notification/push-notification.service';
import { Training } from 'libs/training/training.model';
import { WhereQuery } from 'libs/api/api.provider';
import { tr } from 'date-fns/locale';
import { ContextService } from 'libs/context/context.service';
import { QuestionnaireResponse } from 'libs/questionnaire/questionnaire.model';

type IsUserRegisteredResponse = {
  isUserKnown: boolean;
  isUserRegistered: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject(null);
  currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();
  authGuardUser$: Observable<User | null>;

  private isListeningToUserIds: string[] = [];
  private usersSubject: BehaviorSubject<User[]>[] = [];
  users$: Observable<User[]>[] = [];
  /**
   * getUsers() ouvre un listener Firestore `in` par lot de 30 identifiants
   * (listenToUserChanges). Sans regroupement, chaque lot qui répond
   * republie immédiatement la liste cumulée : jusqu'à ~37 republications
   * pour 1 100 utilisateurs, chacune déclenchant un recalcul complet des
   * statistiques et un nouveau tri/rendu de tableau côté admin (constat du
   * 02/11/2026, rapport-link-up/DIAGNOSTIC_PERFORMANCE_ADMIN_2026-11-02.md).
   * usersBatchArrived$ + debounceTime regroupent les arrivées rapprochées en
   * une seule republication une fois les réponses stabilisées — ni une
   * pagination déguisée, ni un délai arbitraire : la valeur finale est
   * strictement la même, seul le nombre de republications intermédiaires
   * diminue. Une mise à jour isolée ultérieure (inscription, retrait,
   * modification de profil) reste répercutée, avec au plus
   * USERS_BATCH_DEBOUNCE_MS de latence supplémentaire.
   */
  private static readonly USERS_BATCH_DEBOUNCE_MS = 150;
  private usersBatchArrived$: Subject<void> = new Subject<void>();

  private isListeningToAllUsers = false;
  private allUsersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  allUsers$: Observable<User[]> = this.allUsersSubject.asObservable();

  private isListeningToAdminUsers = false;
  private adminUsersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  adminUsers$: Observable<User[]> = this.adminUsersSubject.asObservable();

  private isListeningToNetworkAdminUsers = false;
  private networkAdminUsersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  networkAdminUsers$: Observable<User[]> = this.networkAdminUsersSubject.asObservable();

  private isListeningToAlumni = false;
  private alumniSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  alumni$: Observable<User[]> = this.alumniSubject.asObservable();

  private currentFcmToken: string;
  private currentTrainingCenterId: string;
  private currentNetworkId: string;

  constructor(
    private auth: AuthService,
    private apiService: FirebaseApiProvider,
    private unsubscriberService: UnsubscriberService,
    private pushNotificationService: PushNotificationService,
    private router: Router,
    private contextService: ContextService,
  ) {
    this.authGuardUser$ = this.auth.uid$.pipe(switchMap((uid) => (uid ? this.getAuthGuardUser(uid) : of(null))));

    this.usersBatchArrived$.pipe(debounceTime(UserService.USERS_BATCH_DEBOUNCE_MS)).subscribe(() => {
      this.publishAllUsers();
    });
  }

  private publishAllUsers() {
    this.allUsersSubject.next(
      this.usersSubject.reduce((allusers, subject) => (subject ? [...allusers, ...subject.getValue()] : allusers), []),
    );
  }

  getAuthGuardUser(uid: string): Observable<User | null> {
    const currentUser = this.getCurrentUser();
    if (currentUser?.authId === uid) {
      console.log('getAuthGuardUser FROM CACHE:', currentUser);
      return this.currentUser$;
    }

    console.log('getAuthGuardUser uid:', uid);
    // It runs only when the user logs in or on page refresh
    return from(
      this.apiService.fetchAll<User>('users', User.fromObject, [
        {
          fieldPath: 'authId',
          condition: '==',
          value: uid,
        },
      ]),
    ).pipe(
      map((users) => users[0] ?? null),
      tap((user) => {
        console.log('getAuthGuardUser FETCH:', user);
        if (user?.id) {
          this.currentUserSubject.next(user);
          this.listenToCurrentUser(user.id);
        }
      }),
      finalize(() => console.log('getAuthGuardUser FETCH finalized.')),
    );
  }

  listenToCurrentUser(userId: string) {
    const userSubscriber = this.apiService
      .fetchOne<User>('users', userId, User.fromObject)
      .pipe(finalize(() => console.log('listenToCurrentUser finalized.')))
      .subscribe((user) => {
        const currentUser = this.getCurrentUser();
        if (JSON.stringify(currentUser) !== JSON.stringify(user) && user?.id) {
          console.log('listenToCurrentUser currentUserSubject Next:', user);
          this.currentUserSubject.next(user);
        }
      });
    this.unsubscriberService.add('UserService:listenToCurrentUser', userSubscriber);
  }

  isUserRegistered(email: string, mustBeAdmin = false): Promise<IsUserRegisteredResponse> {
    return firstValueFrom(
      this.apiService.callFunction<IsUserRegisteredResponse>('isUserRegistered', {
        email,
        mustBeAdmin,
      }),
    );
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.getValue();
  }

  updateUser(user: User): Promise<void> {
    return this.apiService.update<User>(`users/${user.id}`, user, User.toObject);
  }

  /**
   * Ajoute une réponse de questionnaire (résultat ou évaluation) via
   * arrayUnion plutôt qu'une réécriture complète du document : deux
   * personnes qui répondent/évaluent en même temps, ou un client qui
   * ressaisit après une coupure, ne s'écrasent plus l'une l'autre. Même
   * mécanisme déjà établi pour les réactions de messages
   * (FirebaseApiProvider.updateArrayField) — voir rapport S4-02.
   */
  async addQuestionnaireResponse(
    userId: string,
    serviceType: 'result' | 'evaluation',
    response: QuestionnaireResponse,
  ): Promise<void> {
    await this.apiService.updateArrayField('users', [userId], `${serviceType}s`, response, 'add');
  }

  /**
   * Suppression admin definitive d'une reponse deja envoyee (S4-03), via
   * arrayRemove plutot qu'une reecriture complete du tableau — meme principe
   * que addQuestionnaireResponse. `response` doit etre l'objet exact tel que
   * lu depuis Firestore (jamais reconstruit a la main) : arrayRemove ne
   * retire que les elements strictement egaux en profondeur, et une reponse
   * envoyee n'est jamais mutee en place une fois persistee — un objet relu
   * depuis un listener live reste donc egal a celui stocke.
   */
  async deleteQuestionnaireResponse(
    userId: string,
    serviceType: 'result' | 'evaluation',
    response: QuestionnaireResponse,
  ): Promise<void> {
    await this.apiService.updateArrayField('users', [userId], `${serviceType}s`, response, 'remove');
  }

  /**
   * Ecriture directe des tableaux results/evaluations, hors du chemin general
   * createOrUpdateUser/updateUser (S4-04 — User.toObject ne les serialise
   * plus, voir son commentaire). Reservee aux rares mutations deliberees et
   * scopees de ces tableaux dans leur ensemble : archivage a la reinscription
   * (reinscription.ts), nettoyage a la suppression d'une formation
   * (trainings.page.ts). Jamais pour ecrire une seule reponse — voir
   * addQuestionnaireResponse/deleteQuestionnaireResponse (arrayUnion/Remove).
   */
  async setArchivedQuestionnaireResponses(
    userId: string,
    results: QuestionnaireResponse[],
    evaluations: QuestionnaireResponse[],
  ): Promise<void> {
    await this.apiService.update(`users/${userId}`, { results, evaluations }, (data) => data);
  }

  async createOrUpdateUser(user: User, updateExistingUser = true): Promise<string> {
    try {
      if (!user?.id || user?.id === '') {
        const existingUser = await this.userExists(user.email);
        if (existingUser) {
          // eslint-disable-next-line no-throw-literal
          throw { code: 'create/user-exists', user: existingUser };
        }

        user.id = await this.apiService.create<User>('users', user, User.toObject);
      } else if (updateExistingUser) {
        await this.updateUser(user);
      }
      return user.id;
    } catch (e) {
      throw e;
    }
  }

  async userExists(email: string): Promise<User | null> {
    if (!email) {
      return null;
    }

    const users = await this.apiService.fetchAll<User>('users', User.fromObject, [
      {
        fieldPath: 'email',
        condition: '==',
        value: email,
      },
    ]);
    return users?.length > 0 ? users[0] : null;
  }

  getUsers(userIds: string[]): Observable<User[]> {
    const userIdsTolistenTo = userIds.filter((userId) => !this.isListeningToUserIds.includes(userId));
    if (userIdsTolistenTo.length > 0 && !this.isListeningToAllUsers) {
      console.log('getUsers API CALL LISTENING:', userIdsTolistenTo);
      this.listenToUserChanges(userIdsTolistenTo);
    } else {
      console.log('getUsers FROM CACHE');
    }

    return this.allUsers$.pipe(
      map((users) => users.filter((user) => userIds.includes(user.id))),
      filter<User[]>((users) =>
        !this.isListeningToAllUsers ? users.length <= this.isListeningToUserIds.length : users.length > 0,
      ),
    );
  }

  getUser(userId: string): Observable<User> {
    return this.apiService.fetchOne<User>('users', userId, User.fromObject);
  }

  private listenToUserChanges(userIds: string[]) {
    this.isListeningToUserIds = [...this.isListeningToUserIds, ...userIds];

    let inQueries = [];
    while (userIds.length > 0) {
      inQueries.push(userIds.splice(0, 30));
    }

    console.log('listenToUserChanges userIds:', inQueries);

    inQueries.forEach((inQuery: string[]) => {
      this.usersSubject.push(new BehaviorSubject<User[]>([]));

      const listenerIndex = this.usersSubject.length - 1;

      this.users$.push(this.usersSubject[listenerIndex].asObservable());

      const usersSubscribe = this.apiService
        .listenToChanges<User>('users', User.fromObject, [
          {
            fieldPath: 'id',
            condition: 'in',
            value: inQuery,
          },
        ])
        .pipe(
          finalize(() => {
            this.isListeningToUserIds = [];
            delete this.usersSubject[listenerIndex];
            delete this.users$[listenerIndex];
            this.allUsersSubject.next([]);
            console.log(`listenToUserChanges ${listenerIndex} finalized.`);
          }),
        )
        .subscribe((users) => {
          console.log(`listenToUserChanges ${listenerIndex} NEXT:`, users);
          this.usersSubject[listenerIndex].next(users);
          // Regroupe les arrivées rapprochées des ~37 lots plutôt que de
          // republier/reconstruire à chacune — voir le commentaire sur
          // usersBatchArrived$ plus haut dans cette classe.
          this.usersBatchArrived$.next();
        });
      this.unsubscriberService.add('UserService:listenToUserChanges', usersSubscribe);
    });
  }

  getAdminUsers(trainingCenterId?: string): Observable<User[]> {
    if (!this.isListeningToAdminUsers || this.currentTrainingCenterId !== trainingCenterId) {
      console.log('getAdminUsers API CALL LISTENING');
      this.listenToAdminUserChanges(trainingCenterId);
    } else {
      console.log('getAdminUsers FROM CACHE');
    }

    return this.adminUsers$;
    // return this.adminUsers$.pipe(filter<User[]>((users) => users.length > 0));
  }

  getNetworkAdminUsers(networkId: string): Observable<User[]> {
    if (!this.isListeningToNetworkAdminUsers || this.currentNetworkId !== networkId) {
      console.log('getNetworkAdminUsers API CALL LISTENING');
      this.listenToNetworkAdminUserChanges(networkId);
    } else {
      console.log('getNetworkAdminUsers FROM CACHE');
    }

    return this.networkAdminUsers$;
  }

  private listenToAdminUserChanges(trainingCenterId?: string) {
    if (this.adminUsersSubject.getValue().length > 0) {
      this.adminUsersSubject.next([]);
    }

    this.isListeningToAdminUsers = true;
    this.currentTrainingCenterId = trainingCenterId;

    const whereQ: WhereQuery[] = [
      {
        fieldPath: 'isAdmin',
        condition: '==',
        value: true,
      },
    ];
    if (trainingCenterId) {
      whereQ.push({
        fieldPath: 'trainingCenterId',
        condition: '==',
        value: trainingCenterId,
      });
    }

    const adminUsersSubscribe = this.apiService
      .listenToChanges<User>('users', User.fromObject, whereQ)
      .pipe(finalize(() => console.log(`listenToAdminUserChanges finalized.`)))
      .subscribe((users) => {
        console.log(`listenToAdminUserChanges NEXT:`, users);
        this.adminUsersSubject.next(users);
      });
    this.unsubscriberService.add('UserService:listenToAdminUserChanges', adminUsersSubscribe);
  }

  private listenToNetworkAdminUserChanges(networkId: string) {
    if (this.networkAdminUsersSubject.getValue().length > 0) {
      this.networkAdminUsersSubject.next([]);
    }

    this.isListeningToNetworkAdminUsers = true;
    this.currentNetworkId = networkId;

    const whereQ: WhereQuery[] = [
      {
        fieldPath: 'isAdmin',
        condition: '==',
        value: true,
      },
      {
        fieldPath: 'networkId',
        condition: '==',
        value: networkId,
      },
    ];

    const networkAdminUsersSubscribe = this.apiService
      .listenToChanges<User>('users', User.fromObject, whereQ)
      .pipe(finalize(() => console.log(`listenToNetworkAdminUserChanges finalized.`)))
      .subscribe((users) => {
        console.log(`listenToNetworkAdminUserChanges NEXT:`, users);
        this.networkAdminUsersSubject.next(users);
      });
    this.unsubscriberService.add('UserService:listenToNetworkAdminUserChanges', networkAdminUsersSubscribe);
  }

  getAllUsers(trainingCenterId?: string): Observable<User[]> {
    if (!this.isListeningToAllUsers) {
      this.isListeningToAllUsers = true;
      console.log('getAllUsers API CALL LISTENING');
      this.listenToAllUserChanges(trainingCenterId);
    } else {
      console.log('getAllUsers FROM CACHE');
    }

    return this.allUsers$.pipe(filter<User[]>((users) => users.length > 0));
  }

  private listenToAllUserChanges(trainingCenterId?: string) {
    if (this.allUsersSubject.getValue().length > 0) {
      this.allUsersSubject.next([]);
    }

    const whereQ: WhereQuery[] = [
      {
        fieldPath: 'trainings',
        condition: '!=',
        value: [],
      },
    ];
    if (trainingCenterId) {
      whereQ.push({
        fieldPath: 'trainingCenterId',
        condition: '==',
        value: trainingCenterId,
      });
    }

    const allUsersSubscribe = this.apiService
      .listenToChanges<User>('users', User.fromObject, whereQ)
      .pipe(
        finalize(() => {
          console.log(`listenToAllUserChanges finalized.`);
          this.isListeningToAllUsers = false;
        }),
      )
      .subscribe((users) => {
        console.log(`listenToAllUserChanges NEXT:`, users);
        this.allUsersSubject.next(users);
      });
    this.unsubscriberService.add('UserService:listenToAllUserChanges', allUsersSubscribe);
  }

  getAlumni(trainingCenterId: string): Observable<User[]> {
    if (!this.isListeningToAlumni) {
      this.isListeningToAlumni = true;
      console.log('getAlumni API CALL LISTENING');
      this.listenToAlumniChanges(trainingCenterId);
    } else {
      console.log('getAlumni FROM CACHE');
    }

    // Avant : `filter(users.length > 0)` — bloquait indéfiniment tout centre
    // dont la recherche alumni/coordonnateur (sur le champ `role` legataire,
    // pas l'affiliation réelle) ne remonte réellement personne. L'écran
    // alumni (aucun état de secours) restait blanc pour toujours — constat
    // remonté par la promotion TFP CAEN 03 le 02/11/2026. alumniSubject est
    // un BehaviorSubject partagé qui republie sa valeur courante (souvent
    // `[]`) de façon SYNCHRONE dès l'abonnement, avant que le listener
    // Firestore n'ait répondu — on écarte seulement cet instantané trivial
    // (même principe que DashboardPage.skipEmptySeed), jamais une liste vide
    // authentique une fois le listener retombé.
    let settled = false;
    Promise.resolve().then(() => {
      settled = true;
    });
    return this.alumni$.pipe(filter((users) => users.length > 0 || settled));
  }

  private listenToAlumniChanges(trainingCenterId: string) {
    if (this.alumniSubject.getValue().length > 0) {
      this.alumniSubject.next([]);
    }

    const alumniSubscribe = this.apiService
      .listenToChanges<User>('users', User.fromObject, [
        {
          fieldPath: 'role',
          condition: 'in',
          value: ['manager', 'alumni'],
        },
        {
          fieldPath: 'trainingCenterId',
          condition: '==',
          value: trainingCenterId,
        },
      ])
      .pipe(
        finalize(() => {
          console.log(`listenToAlumniChanges finalized.`);
          this.isListeningToAlumni = false;
        }),
      )
      .subscribe((users) => {
        console.log(`listenToAlumniChanges NEXT:`, users);
        this.alumniSubject.next(users);
      });
    this.unsubscriberService.add('UserService:listenToAlumniChanges', alumniSubscribe);
  }

  initFcm(user: User) {
    if (
      !this.unsubscriberService.subscribtions['UserService:fcmToken'] ||
      this.unsubscriberService.subscribtions['UserService:fcmToken'][
        this.unsubscriberService.subscribtions['UserService:fcmToken'].length - 1
      ].closed
    ) {
      console.log('INIT FCM');
      this.pushNotificationService.init();
      const fcmSubscriber = this.pushNotificationService.currentFcmToken.subscribe((token) => {
        this.currentFcmToken = token;
        if (token && !user.fcmTokens.includes(token)) {
          console.log('INIT FCM UPDATE TOKEN: ', token);
          user.fcmTokens.push(token);
          this.updateUser(user);
        }
      });

      this.unsubscriberService.add('UserService:fcmToken', fcmSubscriber);
    }
  }

  async logout(isAdmin = false) {
    if (!isAdmin) {
      // Delete FCM Token and Unsubscribe listeners
      const user = this.getCurrentUser();
      const tokenIndex = user.fcmTokens.findIndex((t) => t === this.currentFcmToken);
      user.fcmTokens.splice(tokenIndex, 1);
      await this.updateUser(user);
      await this.pushNotificationService.removeAllListenersAndDeleteToken();
    }

    await this.auth.logout();
    this.router.navigateByUrl('/login', { replaceUrl: true });

    this.unsubscriberService.unsubscribeAll();
    this.currentUserSubject.next(null);
    // Sélecteur de contexte (Sprint 3) : pas de mémorisation d'une session à
    // l'autre — la prochaine connexion redemande le contexte.
    this.contextService.clear();
  }

  async deleteOrUpdateTrainingUsers(users: User[], training?: Training): Promise<void> {
    for (const user of users) {
      let countLinkedApprentices = 0;
      if (user.role === 'tutor') {
        countLinkedApprentices = training?.apprentices?.filter((a) => a.tutors.includes(user.id)).length;
      }

      const trainingIndex = user.trainings.findIndex((id) => id === training?.id);
      if (trainingIndex !== -1 && countLinkedApprentices === 0) {
        user.trainings.splice(trainingIndex, 1);
      }

      if (!user.isRegistered && user.trainings.length === 0) {
        // console.log('delete user: ', user);
        await this.apiService.delete('users', user.id);
      } else {
        await this.apiService.update(`users/${user.id}`, user, User.toObject);
      }
    }
  }

  async fetchTrainingUsers(trainingId: string): Promise<User[]> {
    try {
      return this.apiService.fetchAll<User>('users', User.fromObject, [
        {
          fieldPath: 'trainings',
          condition: 'array-contains',
          value: trainingId,
        },
      ]);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async searchUsers(
    likeField: string,
    likeValue: string,
    role: string,
    trainingCenterId?: string,
    trainingId?: string,
    networkId?: string,
  ): Promise<User[]> {
    console.log('SEARCHUSERS:', likeField, likeValue, role, trainingCenterId, trainingId, networkId);

    // S3-A : plus de filtre de role sur la requete. Un compte existant doit
    // pouvoir etre retrouve quel que soit son role actuel - ex. un tuteur
    // qu'on cherche a rattacher comme stagiaire dans une autre formation
    // n'apparaissait jamais sinon, et l'admin recreait un doublon. `role` est
    // conserve en parametre : il sert encore a `exclureDejaInscrits`
    // ci-dessous, et l'appelant (EditUserComponent) signale a l'admin quand
    // le candidat trouve a deja un role different de l'operation en cours.
    const whereQ: any[] = [];

    if (trainingCenterId) {
      whereQ.push({
        fieldPath: 'trainingCenterId',
        condition: '==',
        value: trainingCenterId,
      });
    }

    if (networkId) {
      whereQ.push({
        fieldPath: 'networkId',
        condition: '==',
        value: networkId,
      });
    }

    // Exclusion des personnes deja inscrites a cette formation, appliquee en
    // memoire avec le filtrage sur le nom. Seuls les tuteurs peuvent etre
    // associes plusieurs fois a une formation.
    const exclureDejaInscrits = role !== 'tutor' && !!trainingId;

    const users = await this.apiService.fetchAll<User>('users', User.fromObject, whereQ);
    console.log('SEARCHUSERS:', users);
    return users.filter((user) => {
      if (!user[likeField] || !user[likeField].toLowerCase().includes(likeValue.toLocaleLowerCase())) {
        return false;
      }
      // Reproduit `trainings not-in [[trainingId]]` : la requete comparait le
      // tableau entier, pas ses elements. Une fiche dont trainings vaut
      // exactement [trainingId] etait exclue, les autres passaient.
      if (exclureDejaInscrits) {
        const t = user.trainings ?? [];
        if (t.length === 1 && t[0] === trainingId) {
          return false;
        }
      }
      return true;
    });
  }
}
